import { researchPillars } from "../data/researchPillars";
import { publications } from "../data/publications";
import { newsItems } from "../data/news";
import { features } from "../data/features";

const VENUE_LIMIT = 4;
/** Venue pills on pillar cards only surface these conferences. */
const PILLAR_VENUE_WHITELIST = new Set(["CHI", "DIS", "CSCW"]);
/** Fallback month when a venue has no month on the pub (CHI→Apr, DIS→Jun, CSCW→Oct). */
const VENUE_MONTH_FALLBACK = { CHI: 4, DIS: 6, CSCW: 10 };

/** Strip HTML wrappers from venue strings (e.g. CHI span tooltips). */
export const stripVenueLabel = (venue) => {
  if (!venue) return "";
  return String(venue)
    .replace(/<[^>]+>/g, "")
    .trim();
};

/**
 * Short conference label for display (e.g. CHI workshop titles → "CHI").
 * Preserves arXiv casing; other leading acronyms stay uppercase.
 */
export const shortVenueLabel = (venue) => {
  const text = stripVenueLabel(venue);
  if (!text) return "";
  if (/^arxiv$/i.test(text) || /^arxiv\b/i.test(text)) return "arXiv";
  const upper = text.toUpperCase();
  for (const name of PILLAR_VENUE_WHITELIST) {
    if (upper === name || upper.startsWith(`${name} `) || upper.includes(` ${name} `)) {
      return name;
    }
  }
  // Leading acronym before a longer title (e.g. "CHI Workshop on…")
  const leading = text.match(/^([A-Za-z]{2,8})\b/);
  return leading ? leading[1].toUpperCase() : text;
};

export const isPreprintPublication = (pub) => {
  const venue = stripVenueLabel(pub.venue);
  const tags = pub.tags || [];
  return (
    /^arxiv$/i.test(venue) ||
    tags.some((tag) => /preprint|arxiv/i.test(tag))
  );
};

/**
 * Display type for a publication: Full Paper, PWiP, Poster, Workshop, Preprint, …
 * Prefer explicit tags (PWiP vs Poster) over a generic "Companion" bucket.
 */
export const getPublicationType = ({ venue, tags = [] } = {}) => {
  const venueText = stripVenueLabel(venue).toLowerCase();
  const normalizedTags = tags.map((tag) => String(tag).toLowerCase());

  if (normalizedTags.some((tag) => tag.includes("workshop"))) {
    return "Workshop";
  }

  if (
    venueText.includes("arxiv") ||
    normalizedTags.some((tag) => tag.includes("preprint"))
  ) {
    return "Preprint";
  }

  if (normalizedTags.some((tag) => tag.includes("pwip"))) {
    return "PWiP";
  }

  if (normalizedTags.some((tag) => tag.includes("poster"))) {
    return "Poster";
  }

  if (
    normalizedTags.some(
      (tag) =>
        tag.includes("companion") || tag.includes("companion publication"),
    )
  ) {
    return "Companion";
  }

  if (
    normalizedTags.some(
      (tag) => tag.includes("full paper") || tag.includes("conference paper"),
    )
  ) {
    return "Full Paper";
  }

  return "Full Paper";
};

/** Venue line for pillar/project publication lists, e.g. "DIS 2026 • PWiP". */
export const formatPillarVenueLine = (pub) => {
  if (isPreprintPublication(pub) && /^arxiv$/i.test(stripVenueLabel(pub.venue))) {
    return ["arXiv preprint", pub.year].filter(Boolean).join(" • ");
  }
  const venue = shortVenueLabel(pub.venue);
  const base = [venue, pub.year].filter(Boolean).join(" ");
  const type = getPublicationType(pub);
  if (!base) return type;
  return `${base} • ${type}`;
};

/** Conference + type line when a workshop title is shown separately, e.g. "CHI 2026 • Workshop". */
export const formatConferenceTypeLine = (pub) => {
  const base = [shortVenueLabel(pub.venue), pub.year].filter(Boolean).join(" ");
  const type = getPublicationType(pub);
  if (!base) return type;
  return `${base} • ${type}`;
};

/**
 * Single venue line for workshop entries, e.g.
 * "CHI 2026 • Workshop on Toward Relationship-Centered Care with AI: …"
 */
export const formatWorkshopVenueLine = (pub) => {
  const workshopTitle = getWorkshopTitle(pub);
  const conf = [shortVenueLabel(pub.venue), pub.year].filter(Boolean).join(" ");
  if (!workshopTitle) return formatConferenceTypeLine(pub);

  // "CHI Workshop on X" or "CHI 2025 Workshop on X" → "Workshop on X"
  let detail = workshopTitle
    .replace(/^[A-Za-z]{2,8}(?:\s+\d{4})?\s+(?=[Ww]orkshop)/, "")
    .trim();
  if (!/^workshop\b/i.test(detail)) {
    detail = `Workshop • ${detail}`;
  }
  if (!conf) return detail;
  return `${conf} • ${detail}`;
};

/**
 * Human-readable workshop title for pillar workshop entries.
 * Prefers full venue when it names a workshop; else parses comments.
 */
export const getWorkshopTitle = (pub) => {
  const venue = stripVenueLabel(pub.venue);
  if (/workshop/i.test(venue)) return venue;
  const fromComments = String(pub.comments || "").match(
    /((?:the\s+)?(?:[A-Z]{2,8}\s+)?(?:\d{4}\s+)?Workshop on [^.,]+)/i,
  );
  if (fromComments) {
    return fromComments[1].replace(/^the\s+/i, "").trim();
  }
  return null;
};

/** Reverse chrono: year, then month (CSCW 2026 → DIS 2026 → CHI 2026 → …). */
export const comparePublicationsByDate = (a, b) => {
  const yearDiff = (b.year || 0) - (a.year || 0);
  if (yearDiff !== 0) return yearDiff;
  return (b.month || 0) - (a.month || 0);
};

/** Parse news/feature date strings like "Jun 2026" or "14 Mar 2026" for sorting. */
const parseLooseDate = (dateStr = "") => {
  const text = String(dateStr).trim();
  const withDay = text.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (withDay) {
    const month = new Date(`${withDay[2]} 1, 2000`).getMonth() + 1;
    return { year: Number(withDay[3]), month: month || 0, day: Number(withDay[1]) };
  }
  const monthYear = text.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYear) {
    const month = new Date(`${monthYear[1]} 1, 2000`).getMonth() + 1;
    return { year: Number(monthYear[2]), month: month || 0, day: 0 };
  }
  const yearOnly = text.match(/(\d{4})/);
  if (yearOnly) return { year: Number(yearOnly[1]), month: 0, day: 0 };
  return { year: 0, month: 0, day: 0 };
};

/** Reverse chrono for news/features: year, then month, then day. */
export const compareLooseDateItems = (a, b) => {
  const da = parseLooseDate(a.date);
  const db = parseLooseDate(b.date);
  const yearDiff = (db.year || 0) - (da.year || 0);
  if (yearDiff !== 0) return yearDiff;
  const monthDiff = (db.month || 0) - (da.month || 0);
  if (monthDiff !== 0) return monthDiff;
  return (db.day || 0) - (da.day || 0);
};

export const isWorkshopPublication = (pub) =>
  (pub.tags || []).some((tag) => /workshop/i.test(tag));

/** Best available public link for a publication. */
export const getPublicationHref = (links = {}) =>
  links.paper || links.preprint || links.pdf || links.poster || null;

/**
 * Unique venue+year pills (CHI / DIS / CSCW only).
 * Returns [{ label, awarded }] e.g. { label: "CHI 2026", awarded: true }.
 * Ordered reverse-chronologically (CSCW 2026 → DIS 2026 → CHI 2026 → …).
 */
export const getVenuesForPublications = (pubs = []) => {
  const byKey = new Map();
  for (const pub of pubs) {
    const venue = shortVenueLabel(pub.venue);
    if (!PILLAR_VENUE_WHITELIST.has(venue)) continue;
    const year = pub.year ? Number(pub.year) : 0;
    const month = pub.month || VENUE_MONTH_FALLBACK[venue] || 0;
    const key = `${venue}|${year}`;
    const awarded = Boolean(pub.award);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        label: year ? `${venue} ${year}` : venue,
        awarded,
        year,
        month,
      });
    } else {
      if (awarded) existing.awarded = true;
      if (month > existing.month) existing.month = month;
    }
  }
  return Array.from(byKey.values())
    .sort(comparePublicationsByDate)
    .slice(0, VENUE_LIMIT)
    .map(({ label, awarded }) => ({ label, awarded }));
};

export const getPillarVenues = (pillarId, pubs = publications) =>
  getVenuesForPublications(
    pubs.filter((pub) => (pub.pillarIds || []).includes(pillarId)),
  );

const pillarById = Object.fromEntries(
  researchPillars.map((pillar) => [pillar.id, pillar]),
);

const linkedToPillar = (item, pillarId) =>
  (item.pillarIds || []).includes(pillarId);

/**
 * Gather cross-linked content for a research pillar briefing.
 * Pubs / talks / media use explicit pillarIds.
 */
export const getPillarContent = (pillarId) => {
  const pillar = pillarById[pillarId];
  if (!pillar) return null;

  const linkedPubs = publications
    .filter((pub) => linkedToPillar(pub, pillarId))
    .sort(comparePublicationsByDate);

  const relatedPublications = linkedPubs.filter((pub) => !isWorkshopPublication(pub));
  const workshops = linkedPubs.filter(isWorkshopPublication);

  const talks = newsItems
    .filter(
      (item) =>
        linkedToPillar(item, pillarId) && item.type === "presentation",
    )
    .sort(compareLooseDateItems);

  const mediaCoverage = features
    .filter((item) => linkedToPillar(item, pillarId))
    .sort(compareLooseDateItems);

  return {
    pillar,
    relatedPublications,
    workshops,
    talks,
    mediaCoverage,
  };
};

export const getPillarById = (pillarId) => pillarById[pillarId] || null;
